import { canPlaceUnder, type NodeTree, type NodeType, type TreeNode } from '@capsio/contracts';

/**
 * 树的内存索引与路径计算。
 *
 * 接口给的是一张平表（每行一个 parentId），层级关系在这儿拼出来。
 * 服务端不给 path 也不给 depth——整棵树都在手上，这些是派生值，
 * 存起来只会多两份要跟着 move 一起维护的真相。
 */

/** 企业本身不是一条节点记录（一套部署一个企业，它已经是单例了），用这个哨兵代表树根。 */
export const ROOT_ID = 'root';

export type TreeIndex = {
  byId: Map<string, TreeNode>;
  /** 父 id → 子节点，已按 sortOrder 排好。根下的那些挂在 ROOT_ID 上。 */
  byParent: Map<string, TreeNode[]>;
  typeByCode: Map<string, NodeType>;
};

export function indexTree(tree: NodeTree): TreeIndex {
  const byId = new Map(tree.nodes.map((n) => [n.id, n]));
  const typeByCode = new Map(tree.types.map((t) => [t.code, t]));
  const byParent = new Map<string, TreeNode[]>();

  for (const node of tree.nodes) {
    const key = node.parentId ?? ROOT_ID;
    const siblings = byParent.get(key);
    if (siblings === undefined) byParent.set(key, [node]);
    else siblings.push(node);
  }

  /*
   * 同一层里资产排在组织节点前面。
   *
   * 一个组织节点下面往往既有它自己的 ATC，也有下级组织节点。ATC 是这个节点自己的东西，
   * 下级是别人的地盘——先看自己的，再往下钻。混着排的话，几份 ATC 会散落在下级之间。
   *
   * 之后才是 sortOrder（人排的顺序），撞了按名字，保证同一份数据每次渲染都一样。
   */
  const rank = (node: TreeNode): number =>
    typeByCode.get(node.typeCode)?.kind === 'ASSET' ? 0 : 1;

  for (const siblings of byParent.values()) {
    siblings.sort(
      (a, b) =>
        rank(a) - rank(b) || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'zh'),
    );
  }

  return { byId, byParent, typeByCode };
}

export function childrenOf(index: TreeIndex, id: string): TreeNode[] {
  return index.byParent.get(id) ?? [];
}

export function typeOf(index: TreeIndex, node: TreeNode): NodeType | undefined {
  return index.typeByCode.get(node.typeCode);
}

/**
 * 日常视野里该看见的子节点：**归档的资产不算**。
 *
 * 归档就是「从日常视野里退下去」，所以树、卡片区、面包屑下拉都用这一份，
 * 三处各写一遍必然漂移。要拿全量（比如 reorder 要求给同层全部节点，
 * 或者归档管理页本来就是要看归档的）用 childrenOf。
 *
 * 组织节点的 enabled=false 是「停用」，不是归档——它还留在架构里，
 * 只是标着停用，所以不过滤。
 */
export function visibleChildrenOf(index: TreeIndex, id: string): TreeNode[] {
  return childrenOf(index, id).filter(
    (node) => node.enabled || typeOf(index, node)?.kind !== 'ASSET',
  );
}

/**
 * 从根到某个节点的 id 链，第一个永远是 ROOT_ID。
 *
 * 指向一个已经不存在的节点（比如刚被别人删掉）就退回只有根——
 * 选中项失效不该让界面白屏，退到根是唯一不需要额外文案的兜底。
 */
export function pathTo(index: TreeIndex, id: string): string[] {
  if (id === ROOT_ID || !index.byId.has(id)) return [ROOT_ID];

  const chain: string[] = [];
  let cursor: string | null = id;
  // 步数上限用节点总数：万一数据里有环，这儿也不会转不出来。
  for (let step = 0; cursor !== null && step <= index.byId.size; step += 1) {
    chain.unshift(cursor);
    cursor = index.byId.get(cursor)?.parentId ?? null;
  }
  return [ROOT_ID, ...chain];
}

/**
 * 某个节点的整支后代，含自己。
 *
 * 「移到哪儿」的候选里必须把这一支排除掉：把节点移进自己的下级，
 * 那一支会连着自己从树上掉下来。后端也拦这件事，但界面不该先给出一个注定被拒的选项。
 */
export function descendantsOf(index: TreeIndex, id: string): Set<string> {
  const result = new Set<string>([id]);
  const queue = [id];
  while (queue.length > 0) {
    for (const child of childrenOf(index, queue.pop()!)) {
      if (result.has(child.id)) continue;
      result.add(child.id);
      queue.push(child.id);
    }
  }
  return result;
}

/**
 * 归属：沿父链上溯，第一个组织单元。
 *
 * 「这份标准归谁负责」是**算出来的**，不是存的字段——所以它永远不会和树对不上。
 * 挂在企业下的 ATC 没有归属部门（全公司通用的标准归企业本身），返回 null。
 */
export function owningUnitOf(index: TreeIndex, id: string): TreeNode | null {
  let cursor = index.byId.get(id)?.parentId ?? null;

  // 步数上限用节点总数：万一数据里有环，这儿也不会转不出来。
  for (let step = 0; cursor !== null && step <= index.byId.size; step += 1) {
    const node = index.byId.get(cursor);
    if (node === undefined) return null;
    if (index.typeByCode.get(node.typeCode)?.kind === 'ORG_UNIT') return node;
    cursor = node.parentId;
  }
  return null;
}

/** 从根到某个节点的名字链，用来在「移到哪儿」的下拉里说清楚是哪一个「甲班」。 */
export function pathNamesOf(index: TreeIndex, id: string, rootName: string): string[] {
  return pathTo(index, id).map((step) =>
    step === ROOT_ID ? rootName : (index.byId.get(step)?.name ?? step),
  );
}

/**
 * 把某个组织单元跟同层上一个 / 下一个**组织单元**换位置，返回这一层的完整新顺序。
 *
 * 返回 null 表示那个方向没有邻居（已经在头或尾），调用方什么都不用做。
 *
 * 两处容易写错，所以单独成函数并且有测试：
 * - reorder 接口要求给同层**全部**节点，含资产。少一个就整条被拒。
 * - 只能交换那两个组织单元的位置，中间夹着的资产必须原地不动——
 *   按一下「上移」顺手把资产也挪了，是用户没要求的副作用。
 */
export function siblingSwapOrder(
  index: TreeIndex,
  parentId: string | null,
  nodeId: string,
  delta: -1 | 1,
): string[] | null {
  const siblings = childrenOf(index, parentId ?? ROOT_ID);
  const ordered = siblings.map((n) => n.id);

  // 组织单元在全量里的下标，按显示顺序。
  const unitSlots = siblings
    .map((n, slot) => (typeOf(index, n)?.kind === 'ASSET' ? -1 : slot))
    .filter((slot) => slot >= 0);

  const at = unitSlots.indexOf(ordered.indexOf(nodeId));
  if (at < 0) return null;

  const from = unitSlots[at];
  const to = unitSlots[at + delta];
  if (from === undefined || to === undefined) return null;

  [ordered[from], ordered[to]] = [ordered[to]!, ordered[from]!];
  return ordered;
}

export type Misplaced = {
  id: string;
  name: string;
  reason: string;
};

/**
 * 按现在的类型规则，哪些已经建好的节点其实放错了地方。
 *
 * 改类型的父规则不会去动现有节点（客户改的是「以后怎么建」，不是把架构推平），
 * 所以这种不一致是正常会出现的。但必须显式说出来——否则它会在下一次移动节点
 * 报错时才被发现，那时已经想不起来是哪次改类型造成的。
 */
export function misplacedNodesOf(tree: NodeTree): Misplaced[] {
  const index = indexTree(tree);
  const bad: Misplaced[] = [];

  for (const node of tree.nodes) {
    const childType = index.typeByCode.get(node.typeCode);
    if (childType === undefined) {
      bad.push({ id: node.id, name: node.name, reason: `类型「${node.typeCode}」已经不存在` });
      continue;
    }

    const parent = node.parentId === null ? null : (index.byId.get(node.parentId) ?? null);
    const parentType = parent === null ? null : (index.typeByCode.get(parent.typeCode) ?? null);
    // 父节点的类型都查不到，问题在父身上，报它自己那一条就够了，别重复报。
    if (parent !== null && parentType === null) continue;

    if (!canPlaceUnder(childType, parentType)) {
      const where = parent === null ? '企业下' : `${parentType?.name}「${parent.name}」下`;
      bad.push({ id: node.id, name: node.name, reason: `${childType.name}不该放在${where}` });
    }
  }

  return bad;
}

/**
 * 这个节点下面能不能挂资产。树行上那个「+」看的是它。
 *
 * 不是所有组织节点都能挂——类型的父规则说了话（某个类型可以设成只在别的类型下面出现，
 * 或者干脆是叶子）。给一个注定被后端拒掉的「+」，比不给更难解释。
 *
 * 类型查不到就当不能挂：那个节点本身已经是坏数据（设置里的「放错位置」清单会报它），
 * 不该在它下面再建东西。
 */
export function canHoldAssetsOf(index: TreeIndex, id: string): boolean {
  const parent = id === ROOT_ID ? null : (index.byId.get(id) ?? null);
  if (id !== ROOT_ID && parent === null) return false;

  const parentType = parent === null ? null : typeOf(index, parent);
  if (parent !== null && parentType === undefined) return false;

  return [...index.typeByCode.values()].some(
    (type) => type.kind === 'ASSET' && canPlaceUnder(type, parentType ?? null),
  );
}

/**
 * 组织结构上的子节点：只要组织单元。侧栏那棵树用它。
 *
 * 资产不进树——侧栏回答「我在组织架构的哪儿」，渲染区回答「这儿有什么」，
 * 一列同时干两件事就是之前不够简单的原因。
 *
 * **筛选时整枝裁剪**（C-181-补1 补-B，原型 `renderTree()` 851–857 行）：
 * 运营视图不显示建设枝，建设视图不显示运营枝——筛选是换一张图看，
 * 不是在原图上打高亮。
 *
 * **整枝裁剪成立的前提：`atc.cls` 随所属部门类别**（C-181 A3 补-3 的服务端
 * 校验就是来保证它的）。**若将来允许运营部门下挂建设卡，这条裁剪就错了——
 * 届时改成只按 ATC 层逐卡过滤，不做部门整枝。**
 * 在此前提落地之前建成的跨类存量（如建设类部门下的运营卡），在筛选视图里
 * 随整枝一起不显示——这是有意取舍，不是漏画。
 */
export function unitChildrenOf(index: TreeIndex, id: string, filter: AtcFilter = 'all'): TreeNode[] {
  return childrenOf(index, id).filter(
    (node) => typeOf(index, node)?.kind === 'ORG_UNIT' && branchKeptOf(index, node, filter),
  );
}

/** 这一枝的类别：部门读自己的 `cls`；岗位随所属部门（投影 `departmentCls`，缺了回溯源父链）。 */
function branchClassOf(index: TreeIndex, node: TreeNode): 'operational' | 'construction' | null {
  if (node.typeCode === 'DEPARTMENT') {
    const cls = node.attributes['cls'];
    return cls === 'operational' || cls === 'construction' ? cls : null;
  }
  const deptCls = node.attributes['departmentCls'];
  if (deptCls === 'operational' || deptCls === 'construction') return deptCls;
  // 投影没带 departmentCls 时沿父链找部门——不因为缺一个投影字段就把整枝藏起来
  const owner = owningUnitOf(index, node.id);
  if (owner !== null && owner.id !== node.id) return branchClassOf(index, owner);
  return null;
}

/** 这一枝在当前筛选下是否保留。`all` 不裁；类别未知的枝**保留**——藏数据比多显示一枝更坏。 */
function branchKeptOf(index: TreeIndex, node: TreeNode, filter: AtcFilter): boolean {
  if (filter === 'all') return true;
  const cls = branchClassOf(index, node);
  return cls === null || cls === filter;
}

/** 某个节点直接挂着的、在用的资产。归档的不算——它们已经从日常视野里退出去了。 */
function ownAssetsOf(index: TreeIndex, id: string): TreeNode[] {
  return visibleChildrenOf(index, id).filter((node) => typeOf(index, node)?.kind === 'ASSET');
}

/**
 * 树上这一站直接挂着的 ATC（C-161 A3：四层一棵树）。
 *
 * 与 `ownAssetsOf` 是同一批，只是**导出**给树用。树此前只列组织单元，
 * ATC 靠行尾一个跳转按钮切到另一页；四层合一之后，ATC 是树的第四层。
 */
export function atcChildrenOf(index: TreeIndex, id: string, filter: AtcFilter = 'all'): TreeNode[] {
  return filterAtcs(ownAssetsOf(index, id), filter);
}

export type AtcGroup = {
  /** 这批 ATC 挂在谁身上。null 表示挂在这一站自己身上。 */
  owner: TreeNode | null;
  /** 从这一站看下去到 owner 的相对路径（不含这一站自己）。owner 为 null 时是空的。 */
  path: string[];
  atcs: TreeNode[];
};

/**
 * 这一站往下整支的 ATC，按「挂在谁身上」分组。
 *
 * 高层级要看得到低层级的东西：选中项目开发部，它下面各个岗位的标准都该出现——
 * 否则一份挂在「全栈工程师」上的标准，只有点到那个岗位才看得见，
 * 而部门负责人根本不知道它存在。
 *
 * 分组而不是摊平：一个部门下二十份标准来自五个岗位，摊平就是一团；
 * 按 owner 分组之后每一段都能读成「全栈工程师的标准」。
 *
 * 顺序：先这一站自己的，再按树的显示顺序往下走——跟侧栏看到的顺序一致。
 * owner 不是直接下级时带上相对路径，否则两个同名的「甲班」分不清。
 */
export function stationAtcGroups(index: TreeIndex, stationId: string): AtcGroup[] {
  const groups: AtcGroup[] = [];

  const own = ownAssetsOf(index, stationId);
  if (own.length > 0) groups.push({ owner: null, path: [], atcs: own });

  const walk = (unitId: string, path: string[]): void => {
    for (const unit of unitChildrenOf(index, unitId)) {
      const here = [...path, unit.name];
      const atcs = ownAssetsOf(index, unit.id);
      if (atcs.length > 0) groups.push({ owner: unit, path: here, atcs });
      walk(unit.id, here);
    }
  };
  walk(stationId, []);

  return groups;
}

/**
 * 算出「当前是哪一站」。
 *
 * 站点不能从选中项推出来。以前 ATC 列表只列直接挂着的，那时「ATC 的归属」正好等于
 * 「你所在的站」；改成整支往下收之后这个等式就不成立了——站在项目开发部、点一份挂在
 * 全栈工程师上的标准，如果按归属算站点，站点就被拽到那个岗位去：树上的高亮跟着跳，
 * ATC 那一列也从「整个部门的」缩成「一个岗位的」，正在挑的兄弟几份全没了。
 *
 * 所以站点是**单独记住的**：只有选中组织节点时才移动它，点 ATC 不动。
 *
 * `pinned` 为 null（刚进来、或者记住的那个节点已经没了）时才回退到从选中项推算——
 * 直接从面包屑下拉里点开一份 ATC 的人从没选过站，那时用它的归属最合理。
 */
export function resolveStationId(
  index: TreeIndex,
  pinned: string | null,
  selectedId: string,
): string {
  if (pinned !== null && (pinned === ROOT_ID || index.byId.has(pinned))) return pinned;

  const selected = selectedId === ROOT_ID ? undefined : index.byId.get(selectedId);
  if (selected === undefined) return ROOT_ID;
  if (typeOf(index, selected)?.kind !== 'ASSET') return selected.id;
  return owningUnitOf(index, selected.id)?.id ?? ROOT_ID;
}

/**
 * 这一站往下整支有几份在用的 ATC。
 *
 * 树行尾的数看的是它。**筛选时只数没被整枝裁掉的枝**（C-181-补1 补-B）——
 * 数是当前视图的数：筛「建设 ATC」时岗位行尾的「1」，指的是这个岗位有 1 张
 * 建设卡，不是它一共有几张。走 `unitChildrenOf` 的裁剪版，与画面严格同一份。
 */
export function subtreeAssetCountOf(
  index: TreeIndex,
  id: string,
  filter: AtcFilter = 'all',
): number {
  let sum = filterAtcs(ownAssetsOf(index, id), filter).length;
  for (const unit of unitChildrenOf(index, id, filter)) {
    sum += subtreeAssetCountOf(index, unit.id, filter);
  }
  return sum;
}

/*
 * ## 运营 / 建设筛选器：字段已落库，装回来了（C-163 A2/A3）
 *
 * 现在接的是 **`atc.cls`**（`operational` / `construction`），不是 `owner_scope`。
 * 两者是两个轴，**不得互相推导**：
 *
 * | 轴 | 列 | 回答 |
 * |---|---|---|
 * | 运营 / 建设 | `cls` | 这张卡干的是哪一类活 |
 * | 客户 / 我方 | `owner_scope` | 这张卡归谁所有 |
 *
 * 建设卡的执行方多为我方、可见方是客户——今天在数据上两轴重合，
 * 明天客户自己建一张建设卡就分道扬镳。
 */

/** 树筛选器的三档。`all` 不筛。 */
export type AtcFilter = 'all' | 'operational' | 'construction';

/** 一张卡是哪一类。读 `attributes.cls`——**库里那一列**，不从别的字段推。 */
export function atcClassOf(node: TreeNode): 'operational' | 'construction' | null {
  const cls = node.attributes['cls'];
  return cls === 'operational' || cls === 'construction' ? cls : null;
}

/** 按筛选器过滤一批卡。`all` 原样返回。 */
export function filterAtcs(atcs: TreeNode[], filter: AtcFilter): TreeNode[] {
  if (filter === 'all') return atcs;
  return atcs.filter((atc) => atcClassOf(atc) === filter);
}

/**
 * 树上任一节点的**生命周期状态**：读 `attributes.status`。
 *
 * 三张资产表的取值各不相同（ATC 七个、岗位四个、部门三个），所以这里**不做归一**，
 * 原样返回库里那个字符串，由调用方按自己那张表去念。
 * 归一成一个联合枚举会立刻造出「部门也能 suspended」这种界面上有、库会拒的状态。
 *
 * 与 `enabled` 不是一回事：`enabled` 是投影视图里算出来的
 * 「status <> 'retired'」，粒度只有两档，答不了「是 draft 还是 active」。
 */
export function statusOf(node: TreeNode): string | null {
  const status = node.attributes['status'];
  return typeof status === 'string' && status !== '' ? status : null;
}

/*
 * ## 上一版为什么撤过一次（留着这段，别再接错）
 *
 * 原型侧栏有「全部 / 运营 ATC / 建设 ATC」三档。**库里没有这个轴的字段。**
 *
 * 唯一形状相近的是 `atc.owner_scope`（client / provider），而那是
 * 「客户的卡 / 我方的卡」——**是另一个轴**，A3 明确写了不得误接。
 * 此前这里用 `owner_scope` 顶了这个筛选器，那是把两个概念焊在了一起：
 * 今天它们在原型数据上恰好重合，明天客户自己建一张建设卡就分道扬镳。
 *
 * 按 A3「无对应字段则整块撤，不留无源控件」——撤。字段该不该加是设计裁定，
 * 已登记待裁，不在这里替设计者决定。
 */

/** 切换器里的一项。 */
export type SwitchOption = {
  id: string;
  name: string;
  /** 类型名。类型被删掉的坏节点为 null——那时不编一个原始 code 出来充当类型。 */
  typeName: string | null;
  /** 就是这一段自己。同级那一组里用它打勾。 */
  here: boolean;
};

export type Crumb = {
  id: string;
  name: string;
  /**
   * 这个节点的类型名。根是企业本身，没有类型记录，为 null——界面上写死「企业」。
   *
   * 每一段都算好，由界面决定显示在哪几段上。
   */
  typeName: string | null;
  /**
   * 编码，**只有资产才给**。组织单元即使有编码（EQUIP、PMD 这种）也是 null。
   *
   * 只给资产是因为这两种编码不是一回事：资产的编码由系统按规则生成、要印到二维码标签上，
   * 是人真的会念出来的一串（「去看一下 ATC-EQU-001」）；组织单元的编码是客户内部维护的
   * 标识，早先明确说过不要在面包屑上显示。
   */
  assetCode: string | null;
  /**
   * 这一段的**同级**，也就是它父节点下的其它组织单元，当前那个 here=true。
   *
   * 只有一个（就是它自己）时返回空数组：一个只列着自己的「同级」分组，没有在提供任何选择。
   * 根没有同级——一套部署一个企业。
   */
  siblings: SwitchOption[];
  /**
   * 这一段自己挂着的、**在用的**资产。归档的不列。
   *
   * 归档就是从日常视野里退下去，而日常视野包括这儿。归档的 ATC 只在设置的归档管理页
   * 出现，那是唯一一处——树、卡片区、搜索、这个切换器都不列它。
   */
  atcs: SwitchOption[];
  /** 这一段下面的组织单元。 */
  orgs: SwitchOption[];
};

/**
 * 把选中的节点摊成面包屑，每一段带上它那一层的**切换器内容**。
 *
 * 一段不是一个标签，是那一层的切换器：名字说「我在这儿」，折角开的下拉说「换一个」。
 * 所以每一段要回答三个「换到哪儿」——同级、这一站的 ATC、下级组织。
 *
 * 三组都算出来交给界面，空的那组由界面决定不画。分组顺序（ATC 在下级组织之前）
 * 跟卡片区一致，那条在卡片区已经裁定过。
 */
export function crumbsFor(index: TreeIndex, selectedId: string, rootName: string): Crumb[] {
  const nameOf = (id: string): string =>
    id === ROOT_ID ? rootName : (index.byId.get(id)?.name ?? id);

  const optionOf = (node: TreeNode, here: boolean): SwitchOption => ({
    id: node.id,
    name: node.name,
    typeName: typeOf(index, node)?.name ?? null,
    here,
  });

  return pathTo(index, selectedId).map((id) => {
    const node = id === ROOT_ID ? undefined : index.byId.get(id);

    /*
     * 同级：父节点下的其它组织单元。资产不进这一组——资产没有「同级」这个概念，
     * 它们在「这一站的 ATC」那一组里，那是另一个问题（「换一份标准」而不是「换一层」）。
     */
    const parentKey = node === undefined ? null : (node.parentId ?? ROOT_ID);
    const siblings =
      parentKey === null
        ? []
        : childrenOf(index, parentKey)
            .filter((sibling) => typeOf(index, sibling)?.kind === 'ORG_UNIT')
            .map((sibling) => optionOf(sibling, sibling.id === id));

    // visibleChildrenOf 而不是 childrenOf：归档的资产不进日常视野，这儿是日常视野。
    const kids = visibleChildrenOf(index, id);

    const type = node === undefined ? undefined : typeOf(index, node);

    return {
      id,
      name: nameOf(id),
      typeName: type?.name ?? null,
      assetCode: type?.kind === 'ASSET' ? (node?.code ?? null) : null,
      // 只剩自己一个时不给这一组：一个只列着自己的下拉分组没有在提供任何选择
      siblings: siblings.length > 1 ? siblings : [],
      atcs: kids
        .filter((kid) => typeOf(index, kid)?.kind === 'ASSET')
        .map((kid) => optionOf(kid, false)),
      orgs: kids
        .filter((kid) => typeOf(index, kid)?.kind === 'ORG_UNIT')
        .map((kid) => optionOf(kid, false)),
    };
  });
}
